import * as React from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import { loadCart, saveCart } from '@utils/cartUtils';
import { Snackbar, Alert, useMediaQuery, useTheme, Chip } from '@mui/material';
import { truncateText, formatPrice } from '@utils/formatter';
import ErrorBoundary from '../error/ErrorBoundary';

// Constants for consistent card dimensions
const CARD_HEIGHT = '450px';
const MOBILE_CARD_HEIGHT = '400px';
const IMAGE_HEIGHT = 180;
const MOBILE_IMAGE_HEIGHT = 150;
const DESCRIPTION_HEIGHT = '3em';
const DESCRIPTION_MAX_LENGTH = 80;
const MOBILE_DESCRIPTION_MAX_LENGTH = 60;

function ProductCard({ product }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Guard: If product is undefined or missing id, render fallback UI
  if (!product || !product.id) {
    return (
      <Card sx={{ width: '100%', height: CARD_HEIGHT, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2 }}>
        <Typography variant="subtitle1" color="error" gutterBottom>Product Data Error</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
          Product information is missing or invalid.
        </Typography>
      </Card>
    );
  }

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [selectedImageIndex, setSelectedImageIndex] = React.useState(0);
  
  // Use product.title or product.name, with a fallback for unnamed products
  const productTitle = product.title || product.name || 'Unnamed Product';
  
  // Handle variant images - use main image as fallback with all possible image fields
  const mainImage = product.image_url_s || product.image_url || product.image || product.image_url_t || `https://placehold.co/400x${isMobile ? MOBILE_IMAGE_HEIGHT : IMAGE_HEIGHT}/e9e9e9/969696?text=${encodeURIComponent(productTitle)}`;
  
  // Enhanced variant images handling - support for multiple fields and colors
  let variantImages = [];
  
  // Collect all available images from the product and its variants
  if (product.variant_images && Array.isArray(product.variant_images)) {
    // Use pre-processed variant_images if available
    variantImages = product.variant_images;
  } else {
    // Fallback to manually collecting images from different fields including the actual API fields
    if (product.image_url_s) variantImages.push(product.image_url_s);
    if (product.image_url && product.image_url !== product.image_url_s) variantImages.push(product.image_url);
    if (product.image && product.image !== product.image_url_s && product.image !== product.image_url) variantImages.push(product.image);
    if (product.image_url_t && product.image_url_t !== product.image_url_s && product.image_url_t !== product.image_url && product.image_url_t !== product.image) variantImages.push(product.image_url_t);
    
    // Add images from arrays if available
    if (Array.isArray(product.image_urls)) variantImages.push(...product.image_urls);
    if (Array.isArray(product.images)) variantImages.push(...product.images);
    
    // Add images from variants if available
    if (Array.isArray(product.variants)) {
      product.variants.forEach(variant => {
        if (variant.images && Array.isArray(variant.images)) {
          variantImages.push(...variant.images);
        }
        if (variant.image_url_s) variantImages.push(variant.image_url_s);
        if (variant.image_url && variant.image_url !== variant.image_url_s) variantImages.push(variant.image_url);
        if (variant.image && variant.image !== variant.image_url_s && variant.image !== variant.image_url) variantImages.push(variant.image);
        if (variant.image_url_t && variant.image_url_t !== variant.image_url_s && variant.image_url_t !== variant.image_url && variant.image_url_t !== variant.image) variantImages.push(variant.image_url_t);
      });
    }
    
    // Remove duplicates and empty values
    variantImages = [...new Set(variantImages)].filter(img => img && img.trim());
  }
  
  // Use all collected images or fallback to main image
  const allImages = variantImages.length > 0 ? variantImages : [mainImage];
  const currentImage = allImages[selectedImageIndex] || mainImage;
  
  // Get variant information
  const variants = product.variants || [];
  const totalVariants = product.total_variants || variants.length;
  
  // Card dimensions based on device size
  const cardHeight = isMobile ? MOBILE_CARD_HEIGHT : CARD_HEIGHT;
  const imgHeight = isMobile ? MOBILE_IMAGE_HEIGHT : IMAGE_HEIGHT;
  const descMaxLength = isMobile ? MOBILE_DESCRIPTION_MAX_LENGTH : DESCRIPTION_MAX_LENGTH;

  const addToCart = (e) => {
    // Prevent navigation when clicking Add to Cart
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const cart = loadCart();
      
      // Determine which variant to add based on current selection
      let productToAdd = { ...product, title: productTitle };
      
      // If there are multiple images and a selection is made, use that variant
      if (allImages.length > 1 && selectedImageIndex > 0) {
        const selectedImage = allImages[selectedImageIndex];
        const variantData = product.variant_data && product.variant_data[selectedImageIndex];
        
        if (variantData) {
          productToAdd = {
            ...product,
            title: productTitle,
            id: variantData.id || product.id,
            sku: variantData.sku,
            image_url: selectedImage,
            variant_info: {
              selected_sku: variantData.sku,
              selected_image: selectedImage,
              parent_id: product.id,
              variant_index: selectedImageIndex
            }
          };
        } else {
          // Fallback if no variant data but different image selected
          productToAdd.image_url = selectedImage;
          productToAdd.variant_info = {
            selected_image: selectedImage,
            parent_id: product.id,
            variant_index: selectedImageIndex
          };
        }
      }
      
      const cartItemId = productToAdd.variant_info ? 
        `${product.id}-${productToAdd.variant_info.selected_sku || selectedImageIndex}` : 
        productToAdd.id;
      
      const existingItemIndex = cart.findIndex(item => item.cart_id === cartItemId);
      
      if (existingItemIndex > -1) {
        cart[existingItemIndex].quantity = (cart[existingItemIndex].quantity || 1) + 1;
      } else {
        cart.push({ 
          ...productToAdd, 
          cart_id: cartItemId,
          quantity: 1,
          image: productToAdd.image_url || productToAdd.image_url_s || mainImage
        });
      }
      
      saveCart(cart);
      
      const variantText = productToAdd.variant_info && productToAdd.variant_info.selected_sku ? 
        ` (${productToAdd.variant_info.selected_sku})` : '';
      setSnackbarMessage(`${productTitle}${variantText} added to cart`);
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to add item to cart');
      setSnackbarOpen(true);
    }
  };

  // Actual product card rendering
  const renderCard = () => (
    <Card 
      component={RouterLink}
      className="lw-product-card"
      data-product-id={product.id}
      id={product.id} // <-- Add this line to set the id prop from PDP/product
      to={`/product/${product.id}`}
      sx={{ 
        width: '100%', 
        height: cardHeight, 
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
        }
      }}
    >
      <CardHeader
        title={
          <Typography
            className='lw-product-title'
            sx={{ 
              fontWeight: 600,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.2,
              height: '2.4em',
              fontSize: isMobile ? '0.95rem' : '1rem'
            }}
          >
            {productTitle} 
          </Typography>
        }
        subheader={
          product.manufacturer ? (
            <span style={{ color: 'inherit', fontSize: isMobile ? '0.8rem' : '0.875rem' }} onClick={e => e.stopPropagation()}>
              {product.manufacturer}
            </span>
          ) : null
        }
        sx={{ pb: isMobile ? 0.5 : 1 }}
      />
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height={imgHeight}
          image={currentImage}
          alt={productTitle} 
          sx={{ 
            objectFit: 'contain', 
            background: '#f9f9f9', 
            p: 2,
            width: '100%',
            transition: 'transform 0.3s ease-in-out',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        />
        
        {/* Variant Images Thumbnail Gallery */}
        {allImages.length > 1 && (
          <Box 
            sx={{ 
              position: 'absolute', 
              bottom: 4, 
              left: 4, 
              right: 4,
              display: 'flex',
              gap: 0.25,
              overflowX: 'auto',
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 1,
              p: 0.5,
              maxHeight: 40,
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Options label */}
            <Box 
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 45,
                height: 28,
                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                borderRadius: 0.5,
                mr: 0.5,
                flexShrink: 0,
                border: '1px solid rgba(25, 118, 210, 0.2)'
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
              >
                Options
              </Typography>
            </Box>
            
            {allImages.slice(0, 6).map((img, index) => (
              <Box
                key={index}
                component="img"
                src={img}
                alt={`Variant ${index + 1}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedImageIndex(index);
                }}
                sx={{
                  width: 28,
                  height: 28,
                  objectFit: 'cover',
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  border: selectedImageIndex === index ? '2px solid' : '1px solid',
                  borderColor: selectedImageIndex === index ? 'primary.main' : 'grey.300',
                  opacity: selectedImageIndex === index ? 1 : 0.8,
                  boxSizing: 'border-box',
                  flexShrink: 0,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    opacity: 1,
                    transform: 'scale(1.05)',
                    borderColor: 'primary.main'
                  }
                }}
              />
            ))}
            {allImages.length > 6 && (
              <Box 
                sx={{
                  width: 28, 
                  height: 28, 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'grey.100',
                  borderRadius: 0.5,
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  color: 'text.secondary',
                  flexShrink: 0,
                  border: '1px solid',
                  borderColor: 'grey.300',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'grey.200',
                    color: 'primary.main',
                    borderColor: 'primary.main'
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // Cycle through images
                  setSelectedImageIndex((selectedImageIndex + 1) % allImages.length);
                }}
              >
                +{allImages.length - 6}
              </Box>
            )}
          </Box>
        )}
        
        {/* Variant Count Badge */}
        {totalVariants > 1 && (
          <Chip
            label={`${totalVariants} options`}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'primary.main',
              color: 'white',
              fontSize: '0.7rem'
            }}
          />
        )}
      </Box>
      <CardContent sx={{ flexGrow: 1, py: isMobile ? 1 : 2 }}>
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 1, 
            height: DESCRIPTION_HEIGHT, 
            overflow: 'hidden', 
            display: '-webkit-box', 
            WebkitLineClamp: 2, 
            WebkitBoxOrient: 'vertical',
            fontSize: isMobile ? '0.75rem' : '0.875rem'
          }}
        >
          {truncateText(product.description || '', descMaxLength)}
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 700,
            fontSize: isMobile ? '1rem' : '1.25rem',
            color: 'primary.main'
          }}
          className='lw-price'
        >
          {formatPrice(product.price, 'USD', 2) || 'Price unavailable'} 
        </Typography>
      </CardContent>
      <CardActions sx={{ padding: isMobile ? 1 : 2, pt: 0 }}>
        <Button 
          variant="contained"
          className='lw-cart-add-trigger'
          size={isMobile ? 'small' : 'medium'}
          fullWidth
          startIcon={<ShoppingCartIcon />}
          onClick={(e) => addToCart(e)}
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            '&:hover': {
              backgroundColor: 'primary.dark'
            },
            position: 'relative',
            zIndex: 10, // Ensure clickable on top of RouterLink
            fontSize: isMobile ? '0.8rem' : '0.875rem'
          }}
        >
          Add to Cart
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <>
      <ErrorBoundary fallbackComponent={ProductCardFallback}> 
        {renderCard()}
      </ErrorBoundary>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarMessage.startsWith('Failed') ? 'error' : 'success'} sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

const ProductCardFallback = ({ resetErrorBoundary }) => (
  <Card sx={{ width: '100%', height: CARD_HEIGHT, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 2 }}>
    <Typography variant='subtitle1' color='error' gutterBottom>Product Display Error</Typography>
    <Typography variant='body2' color='text.secondary' sx={{ mb: 2, textAlign: 'center' }}>
      We couldn't display this product properly.
    </Typography>
    <Button 
      variant='outlined' 
      size='small' 
      onClick={resetErrorBoundary}
      sx={{ mt: 1 }}
    >
      Try Again
    </Button>
  </Card>
);

const MemoizedProductCard = React.memo(ProductCard);

export default MemoizedProductCard;
